import { useLocation } from 'react-router-dom';

interface SchemaProps {
  schema: Record<string, unknown>;
}

export function JsonLd({ schema }: SchemaProps) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function makeWebApplicationSchema(
  name = 'ai-free-resume',
  description = 'Free AI resume builder. No sign-up required. Create, edit, and download your resume as a PDF.',
  offers: Record<string, unknown> = {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  }
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Any',
    offers,
    description,
    url: 'https://ai-free-resume.org',
    browserRequirements: 'Requires JavaScript. HTML5 compatible browser.',
    featureList: 'AI bullet rewriter, ATS-friendly templates, PDF export, local storage, no sign-up',
  };
}

export function makeFAQSchema(items: { question: string; answer: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function makeBreadcrumbSchema(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://ai-free-resume.org${item.path === '/' ? '' : item.path}`,
    })),
  };
}

export function makeHowToSchema(
  name: string,
  description: string,
  steps: string[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      text: step,
    })),
  };
}

export function makeItemListSchema(items: { name: string; description: string; url: string }[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      description: item.description,
      url: item.url,
    })),
  };
}

export function BreadcrumbSchema({ items }: { items: { name: string; path: string }[] }) {
  const location = useLocation();
  const fullItems = [{ name: 'Home', path: '/' }, ...items.filter((i) => i.path !== location.pathname)];
  return <JsonLd schema={makeBreadcrumbSchema(fullItems)} />;
}

export default JsonLd;
